/** @jsxImportSource preact */
import type { ComponentChildren } from "preact";
import { useRef } from "preact/hooks";

import * as m from "../../paraglide/messages.js";
import { ChevronUpIcon } from "../icons";
import {
  WidgetCharacter,
  type WidgetCharacterVariant,
} from "../widget-character";

interface PeekBarProps {
  summary: ComponentChildren;
  /** 买家刚点了跳转、页面还没换走：角色打盹，摘要是灰字。 */
  dozing: boolean;
  /** 新页面首帧就在：闭着眼，400ms 后睁眼眨一次、摘要变黑（设计稿 12a F2→F3）。 */
  waking: boolean;
  /** 收起时往下淡出，抽屉从它身后升起。 */
  leaving: boolean;
  /** 离视口底边的距离：让开店面吸底的购买栏。 */
  lift: number;
  color: string;
  eyeColor: string;
  variant: WidgetCharacterVariant;
  radius: number;
  font: string;
  fontSize: string;
  onExpand: () => void;
  onDismiss: () => void;
}

/** 上下划过这么远才算划，否则按点处理。 */
const SWIPE_PX = 12;
/** 28px 角色的圆角，和设计稿窥视条里的角色一致。 */
const CHARACTER_RADIUS = "9px";

/**
 * 窥视条（设计稿 12a）：在抽屉里点了跳转，抽屉不关，而是降成底部一条，
 * 露出新页面；跨页面、刷新都保持这个样子。点一下或上划回到抽屉，下划收起聊天窗。
 */
export function PeekBar({
  summary,
  dozing,
  waking,
  leaving,
  lift,
  color,
  eyeColor,
  variant,
  radius,
  font,
  fontSize,
  onExpand,
  onDismiss,
}: PeekBarProps) {
  const start = useRef<number>();
  const swiped = useRef(false);
  return (
    <div
      class="gn-peek"
      data-wake={waking || undefined}
      data-dozing={dozing || undefined}
      data-leaving={leaving || undefined}
      inert={leaving || undefined}
      style={{
        "--gn-peek-lift": `${String(lift)}px`,
        // 贴着屏幕底边时底部留出安全区；浮在购买栏上方就是一整块圆角卡片。
        borderRadius:
          lift > 0
            ? `${String(radius)}px`
            : `${String(radius)}px ${String(radius)}px 0 0`,
        paddingBottom:
          lift > 0 ? 12 : "calc(12px + env(safe-area-inset-bottom, 0px))",
        fontFamily: font,
        fontSize,
      }}
      onPointerDown={(event) => {
        start.current = event.clientY;
        swiped.current = false;
      }}
      onPointerUp={(event) => {
        if (start.current === undefined) return;
        const moved = event.clientY - start.current;
        start.current = undefined;
        if (Math.abs(moved) < SWIPE_PX) return;
        swiped.current = true;
        if (moved < 0) onExpand();
        else onDismiss();
      }}
    >
      <span class="gn-peek-handle" aria-hidden="true" />
      <button
        class="gn-peek-open"
        type="button"
        aria-label={m.widget_peek_open()}
        onClick={() => {
          if (swiped.current) return;
          onExpand();
        }}
      >
        <span class="gn-peek-character">
          <WidgetCharacter
            size={28}
            color={color}
            eyeColor={eyeColor}
            radius={CHARACTER_RADIUS}
            motion={dozing ? "dozing" : "quiet"}
            variant={variant}
          />
        </span>
        <span class="gn-peek-summary">{summary}</span>
        <span class="gn-peek-chevron">
          <ChevronUpIcon size={18} />
        </span>
      </button>
    </div>
  );
}

/**
 * 窥视条跟抽屉同一套底色和圆角；摘要和箭头的明暗跟着角色醒没醒。
 * 从抽屉降下来时，等抽屉快沉到底（260ms）才浮出来，不然两层内容会叠在一起。
 */
export const PEEK_STYLES = `
.gn-peek { position:absolute; left:0; bottom:var(--gn-peek-lift); z-index:1; box-sizing:border-box; width:var(--gn-viewport-width); padding:8px 16px 12px; background:var(--gn-drawer-fill); box-shadow:0 -8px 28px rgba(28,27,26,.12); touch-action:none; user-select:none; pointer-events:auto; animation:gnFadeIn .12s .16s both; }
.gn-peek[data-wake] { animation:none; }
.gn-peek[data-leaving] { opacity:0; transition:opacity .2s ease; }
/* 升回抽屉时角色不飞，原地缩小淡出（设计稿 10a B2）。 */
.gn-peek[data-leaving] .gn-peek-character { transform:scale(.6); transition:transform .2s cubic-bezier(.2,.8,.2,1); }
.gn-peek-handle { display:block; width:36px; height:4px; margin:0 auto 8px; border-radius:2px; background:currentColor; color:var(--gn-peek-muted); opacity:.3; }
.gn-peek-open { display:flex; align-items:center; gap:10px; width:100%; min-height:32px; padding:0; border:0; background:transparent; color:var(--gn-peek-ink); font:inherit; text-align:start; cursor:pointer; -webkit-tap-highlight-color:transparent; }
.gn-peek-summary { flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; color:var(--gn-peek-ink); transition:color .2s ease; }
.gn-peek-chevron { display:grid; flex:none; color:var(--gn-peek-accent); transition:color .2s ease; }
.gn-peek[data-dozing] .gn-peek-summary, .gn-peek[data-dozing] .gn-peek-chevron { color:var(--gn-peek-muted); }
@keyframes gnPeekSummary { 0%, 71% { color:var(--gn-peek-muted); } 86%, 100% { color:var(--gn-peek-ink); } }
@keyframes gnPeekChevron { 0%, 71% { color:var(--gn-peek-muted); } 86%, 100% { color:var(--gn-peek-accent); } }
.gn-peek[data-wake] .gn-peek-summary { animation:gnPeekSummary .56s linear backwards; }
.gn-peek[data-wake] .gn-peek-chevron { animation:gnPeekChevron .56s linear backwards; }
`;
